/**
 * Formulaire plat d'édition de chantier.
 * Utilisé dans ChantierFormDialog en mode édition.
 * Tous les champs sont affichés en une seule page (pas de stepper).
 */

import { HelpCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { labels, placeholders } from "@/lib/content";
import { PictogrammeSelector } from "@/components/chantiers/PictogrammeSelector";
import { CouleurSelector } from "@/components/chantiers/CouleurSelector";
import { PreviewChantier } from "@/components/chantiers/PreviewChantier";
import type { StatutCreationOption } from "@/constants/statuts";

interface ChantierEditFormProps {
  formData: {
    nom_chantier: string;
    reference_chantier: string;
    client: string;
    adresse: string;
    duree_estimee_jours: number;
    budget_ht: number;
    description: string;
    etat_chantier: string;
    date_debut_prevue: string;
    date_fin_estimee: string;
    date_fin_reelle: string;
    pictogramme: string;
    couleur: string;
  };
  updateField: (field: string, value: any) => void;
  setDureeManuelleModifiee: (v: boolean) => void;
  statutsDisponibles: StatutCreationOption[];
  showBudgetField?: boolean;
}

export function ChantierEditForm({
  formData,
  updateField,
  setDureeManuelleModifiee,
  statutsDisponibles,
  showBudgetField = false,
}: ChantierEditFormProps) {
  return (
    <div className="grid gap-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="nom_chantier" className="font-semibold">{labels.forms.projectName} *</Label>
        <Input
          id="nom_chantier"
          value={formData.nom_chantier}
          onChange={(e) => updateField("nom_chantier", e.target.value)}
          placeholder={placeholders.project.name}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="reference_chantier" className="font-semibold">
          Référence du chantier *
        </Label>
        <Input
          id="reference_chantier"
          value={formData.reference_chantier}
          onChange={(e) => updateField("reference_chantier", e.target.value)}
          placeholder="Ex: REF-2025-001"
          required
        />
        <p className="text-xs text-foreground italic">
          (Veuillez utiliser la même référence que dans vos factures fournisseurs)
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="client" className="font-semibold">{labels.forms.projectClient} *</Label>
        <Input
          id="client"
          value={formData.client}
          onChange={(e) => updateField("client", e.target.value)}
          placeholder={placeholders.project.client}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="adresse" className="font-semibold">{labels.forms.projectAddress} *</Label>
        <Input
          id="adresse"
          value={formData.adresse}
          onChange={(e) => updateField("adresse", e.target.value)}
          placeholder={placeholders.project.address}
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="date_debut_prevue" className="font-semibold">Date de début *</Label>
          <Input
            id="date_debut_prevue"
            type="date"
            value={formData.date_debut_prevue}
            onChange={(e) => updateField("date_debut_prevue", e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="date_fin_estimee" className="font-semibold">Date de fin estimée</Label>
          <Input
            id="date_fin_estimee"
            type="date"
            value={formData.date_fin_estimee}
            onChange={(e) => updateField("date_fin_estimee", e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="duree_estimee_jours" className="font-semibold">Durée estimée (jours travaillés)</Label>
        <Input
          id="duree_estimee_jours"
          type="number"
          min="0.5"
          step="0.5"
          value={formData.duree_estimee_jours}
          onChange={(e) => {
            setDureeManuelleModifiee(true);
            updateField("duree_estimee_jours", parseFloat(e.target.value) || 0.5);
          }}
        />
        <p className="text-xs text-muted-foreground">Calculé automatiquement, modifiable si besoin (accepte les demi-journées: 3.5)</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description" className="font-semibold">{labels.forms.projectDescription}</Label>
        <Input
          id="description"
          value={formData.description}
          onChange={(e) => updateField("description", e.target.value)}
          placeholder="Détails du chantier..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="etat_chantier" className="font-semibold">État du chantier *</Label>
        <Select
          value={formData.etat_chantier}
          onValueChange={(value) => updateField("etat_chantier", value)}
        >
          <SelectTrigger id="etat_chantier">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <TooltipProvider delayDuration={300}>
              {statutsDisponibles.map((statut) => (
                <Tooltip key={statut.value}>
                  <TooltipTrigger asChild>
                    <div>
                      <SelectItem
                        value={statut.value}
                        disabled={statut.disabled}
                        className={statut.disabled ? 'opacity-50' : ''}
                      >
                        {statut.emoji} {statut.label}
                      </SelectItem>
                    </div>
                  </TooltipTrigger>
                  {statut.disabled && statut.reason && (
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-sm">{statut.reason}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
            </TooltipProvider>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">Brouillon : vous préparez. Projection : vous simulez les coûts. En cours : le chantier a démarré sur le terrain.</p>
      </div>

      {/* Pictogramme et couleur */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Label className="font-semibold">Pictogramme *</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[250px]">
                  <p className="text-xs">Choisissez une icône pour reconnaître ce chantier d'un coup d'œil dans le dashboard et les rapports</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <PictogrammeSelector
            value={formData.pictogramme || null}
            onChange={(id) => updateField("pictogramme", id)}
            couleur={formData.couleur || '#3b82f6'}
            error={false}
          />
        </div>
        <div className="space-y-2">
          <Label className="font-semibold">Couleur *</Label>
          <CouleurSelector
            value={formData.couleur || null}
            onChange={(hex) => updateField("couleur", hex)}
            error={false}
          />
        </div>
      </div>

      {/* Aperçu */}
      <PreviewChantier
        pictogrammeId={formData.pictogramme || null}
        couleurHex={formData.couleur || null}
        nomChantier={formData.nom_chantier}
      />

      {/* Budget HT — uniquement en mode édition depuis ProjectDetail */}
      {showBudgetField && (
        <div className="space-y-2">
          <Label htmlFor="budget_ht" className="font-semibold">Budget HT (€)</Label>
          <Input
            id="budget_ht"
            type="number"
            step="0.01"
            value={formData.budget_ht}
            onChange={(e) => updateField("budget_ht", parseFloat(e.target.value) || 0)}
            placeholder="0.00"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="date_fin_reelle" className="font-semibold">Date de fin réelle</Label>
        <Input
          id="date_fin_reelle"
          type="date"
          value={formData.date_fin_reelle}
          onChange={(e) => updateField("date_fin_reelle", e.target.value)}
        />
        <p className="text-xs text-foreground font-medium">À remplir à la fin du chantier</p>
      </div>
    </div>
  );
}
